import { Navigate, Route, Routes } from 'react-router-dom'

import { RotaAutenticada, RotaComPermissao } from './autenticacao/rota-protegida'
import { LayoutApp } from './layout/layout-app'
import { PaginaAgendamentos } from './paginas/agendamentos/agendamentos'
import { PaginaEntrar } from './paginas/autenticacao/entrar'
import { PaginaEsqueciMinhaSenha } from './paginas/autenticacao/esqueci-minha-senha'
import { PaginaRedefinirSenha } from './paginas/autenticacao/redefinir-senha'
import { PaginaNaoEncontrada, PaginaNotificacoes, PaginaSair } from './paginas/auxiliares'
import {
  PaginaMotivosAgendamento,
  PaginaMotivosVendaNaoRealizada,
  PaginaPontosConversao,
  PaginaProdutosAssociados,
  PaginaTags,
} from './paginas/configuracoes/catalogos'
import { PaginaAuditoria } from './paginas/configuracoes/auditoria'
import { PaginaEquipes } from './paginas/configuracoes/equipes'
import { PaginaUnidades } from './paginas/configuracoes/unidades'
import { PaginaDashboard } from './paginas/dashboard'
import { PaginaDetalheLead } from './paginas/leads/detalhe-lead'
import { PaginaEditarLead } from './paginas/leads/editar-lead'
import { PaginaListaLeads } from './paginas/leads/lista-leads'
import { PaginaNovoLead } from './paginas/leads/novo-lead'
import { PaginaMetaAdsContas } from './paginas/meta-ads/contas'
import { PaginaMetaAdsDiagnostico } from './paginas/meta-ads/diagnostico'
import { PaginaMetaAdsHierarquia } from './paginas/meta-ads/hierarquia'
import { PaginaMetaAdsMetricas } from './paginas/meta-ads/metricas'
import { PaginaMetaAdsOtimizacoes } from './paginas/meta-ads/otimizacoes'
import { PaginaMetaAdsOtimizador } from './paginas/meta-ads/otimizador'
import { PaginaMetaAdsRecomendacoes } from './paginas/meta-ads/recomendacoes'
import { PaginaMetaAdsVisaoGeral } from './paginas/meta-ads/visao-geral'
import { PaginaPainelAtendimentos } from './paginas/painel-atendimentos'
import { PaginaPerfil } from './paginas/perfil'
import { PaginaFunil } from './paginas/relatorios/funil'
import { PaginaPerdas } from './paginas/relatorios/perdas'
import {
  PaginaRelatorioPropostas,
  PaginaRelatorioVendas,
} from './paginas/relatorios/relatorios-pendentes'
import { PaginaFormularioUsuario } from './paginas/usuarios/formulario-usuario'
import { PaginaListaUsuarios } from './paginas/usuarios/lista-usuarios'

/**
 * Rotas da aplicação.
 *
 * Cada grupo declara **as mesmas permissões que o backend exige** na rota
 * correspondente. Isso não é autorização — é experiência: o backend nega de todo
 * modo (RNF-20), e o portão aqui existe para o usuário ver "você não tem acesso" em
 * vez de uma tela cheia de `403`.
 *
 * `/perfil` fica fora de qualquer portão de permissão: ações sobre a própria conta
 * são de todo papel (`04-roles §3.1`), e é para lá que a rota protegida manda quem
 * precisa trocar a senha antes de usar o sistema (RN-06).
 */
export function App() {
  return (
    <Routes>
      {/* --- Públicas (TELA-00, 00A, 00B) --- */}
      <Route path="/entrar" element={<PaginaEntrar />} />
      <Route path="/esqueci-minha-senha" element={<PaginaEsqueciMinhaSenha />} />
      <Route path="/redefinir-senha" element={<PaginaRedefinirSenha />} />
      {/* O link do e-mail de convite usa o mesmo endpoint, com `?convite=1`. */}
      <Route path="/definir-senha" element={<PaginaRedefinirSenha />} />

      {/* --- Autenticadas --- */}
      <Route element={<RotaAutenticada />}>
        <Route path="/sair" element={<PaginaSair />} />

        <Route element={<LayoutApp />}>
          <Route path="/perfil" element={<PaginaPerfil />} />
          <Route path="/notificacoes" element={<PaginaNotificacoes />} />

          <Route element={<RotaComPermissao permissoes={['DASHBOARD_LER']} />}>
            <Route index element={<PaginaDashboard />} />
          </Route>

          <Route element={<RotaComPermissao permissoes={['PAINEL_LER']} />}>
            <Route path="/atendimentos" element={<PaginaPainelAtendimentos />} />
          </Route>

          <Route element={<RotaComPermissao permissoes={['LEAD_LER']} />}>
            <Route path="/leads" element={<PaginaListaLeads />} />
            <Route path="/leads/:uuid" element={<PaginaDetalheLead />} />
          </Route>

          <Route element={<RotaComPermissao permissoes={['LEAD_CRIAR']} />}>
            <Route path="/leads/novo" element={<PaginaNovoLead />} />
          </Route>

          <Route element={<RotaComPermissao permissoes={['LEAD_EDITAR']} />}>
            <Route path="/leads/:uuid/editar" element={<PaginaEditarLead />} />
          </Route>

          <Route element={<RotaComPermissao permissoes={['AGENDAMENTO_LER']} />}>
            <Route path="/agendamentos" element={<PaginaAgendamentos />} />
          </Route>

          <Route element={<RotaComPermissao permissoes={['USUARIO_LER']} />}>
            <Route path="/usuarios" element={<PaginaListaUsuarios />} />
            <Route path="/usuarios/:uuid" element={<PaginaFormularioUsuario />} />
          </Route>

          <Route element={<RotaComPermissao permissoes={['USUARIO_CRIAR']} />}>
            <Route path="/usuarios/novo" element={<PaginaFormularioUsuario />} />
          </Route>

          {/*
            --- Meta Ads ---
            Três portões, espelhando as três permissões do backend. `META_ADS_ANALISAR`
            protege o que **custa dinheiro** (cada análise narrada e cada pergunta ao
            Otimizador é uma chamada paga a um provedor de IA), e `META_ADS_GERENCIAR` o que
            mexe em dinheiro veiculado.
          */}
          <Route element={<RotaComPermissao permissoes={['META_ADS_LER']} />}>
            <Route path="/meta-ads" element={<PaginaMetaAdsVisaoGeral />} />
            <Route path="/meta-ads/campanhas" element={<PaginaMetaAdsHierarquia />} />
            <Route path="/meta-ads/metricas" element={<PaginaMetaAdsMetricas />} />
            <Route path="/meta-ads/diagnostico" element={<PaginaMetaAdsDiagnostico />} />
            <Route path="/meta-ads/otimizacoes" element={<PaginaMetaAdsOtimizacoes />} />
          </Route>

          <Route element={<RotaComPermissao permissoes={['META_ADS_ANALISAR']} />}>
            <Route path="/meta-ads/recomendacoes" element={<PaginaMetaAdsRecomendacoes />} />
            <Route path="/meta-ads/otimizador" element={<PaginaMetaAdsOtimizador />} />
          </Route>

          <Route element={<RotaComPermissao permissoes={['META_ADS_GERENCIAR']} />}>
            <Route path="/meta-ads/contas" element={<PaginaMetaAdsContas />} />
          </Route>

          {/* --- Relatórios --- */}
          <Route element={<RotaComPermissao permissoes={['RELATORIO_VENDAS_LER']} />}>
            <Route path="/relatorios" element={<Navigate to="/relatorios/funil" replace />} />
            <Route path="/relatorios/funil" element={<PaginaFunil />} />
            <Route path="/relatorios/perdas" element={<PaginaPerdas />} />
            <Route path="/relatorios/vendas" element={<PaginaRelatorioVendas />} />
          </Route>

          <Route element={<RotaComPermissao permissoes={['RELATORIO_PROPOSTAS_LER']} />}>
            <Route path="/relatorios/propostas" element={<PaginaRelatorioPropostas />} />
          </Route>

          {/* --- Configurações --- */}
          <Route element={<RotaComPermissao permissoes={['UNIDADE_GERENCIAR']} />}>
            <Route path="/configuracoes/unidades" element={<PaginaUnidades />} />
          </Route>

          <Route element={<RotaComPermissao permissoes={['EQUIPE_GERENCIAR']} />}>
            <Route path="/configuracoes/equipes" element={<PaginaEquipes />} />
          </Route>

          {/**
           * Catálogos usam `CATALOGO_LER` no portão de rota, e não a permissão de
           * gerência: todos os papéis leem catálogo (é o que preenche os selects das
           * telas de operação), e a tela desabilita a edição para quem não pode
           * gerenciar. Fechar a rota por `CATALOGO_GERENCIAR_*` esconderia do
           * atendente a lista que ele usa para conferir um motivo.
           */}
          <Route element={<RotaComPermissao permissoes={['CATALOGO_LER']} />}>
            <Route path="/configuracoes" element={<Navigate to="/configuracoes/unidades" replace />} />
            <Route path="/configuracoes/pontos-conversao" element={<PaginaPontosConversao />} />
            <Route path="/configuracoes/produtos-associados" element={<PaginaProdutosAssociados />} />
            <Route path="/configuracoes/motivos-agendamento" element={<PaginaMotivosAgendamento />} />
            <Route
              path="/configuracoes/motivos-venda-nao-realizada"
              element={<PaginaMotivosVendaNaoRealizada />}
            />
            <Route path="/configuracoes/tags" element={<PaginaTags />} />
          </Route>

          <Route element={<RotaComPermissao permissoes={['AUDITORIA_LER']} />}>
            <Route path="/configuracoes/auditoria" element={<PaginaAuditoria />} />
          </Route>

          <Route path="*" element={<PaginaNaoEncontrada />} />
        </Route>
      </Route>
    </Routes>
  )
}
